import { useEffect, useMemo, useState } from 'react';
import {
  UsersIcon,
  IdentificationIcon,
  BanknotesIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { useAdminStats } from '../hooks/useAdminStats';
import { useAdminTrend } from '../hooks/useAdminTrend';
import { useAuth } from '../../../core/providers/AuthProvider';
import { useStudents } from '../../Students/hooks/useStudents';
import { usePayments } from '../../Payments/hooks/usePayments';
import { ROLES } from '../../../shared/constant/roles';
import { GRADE_LEVELS } from '../../../shared/constant/gradeLevels';
import { SCHOOL_FEES, TOTAL_SCHOOL_FEE, DISCOUNT_CATEGORY } from '../../../shared/constant/schoolFees';
import { compareByGradeOrder } from '../../../shared/utils/sortByGrade';
import { getDateRange, monthNames, schoolYearLabel } from '../../../shared/utils/reportingPeriod';
import { peso, pesoWhole } from '../../../shared/utils/formatCurrency';
import { useReportingPeriod } from '../../../shared/hooks/useReportingPeriod';
import ReportingPeriodBar from '../../../shared/components/ReportingPeriodBar';
import KpiCard, { kpiTones, signTone } from '../../../shared/components/KpiCard';
import MonthlyTrendChart from '../../../shared/components/MonthlyTrendChart';
import CategoryAdjustments from '../../../shared/components/CategoryAdjustments';

const quickLinks = [
  { label: 'Add Student', icon: IdentificationIcon, tab: 'Students' },
  { label: 'Add Payment', icon: BanknotesIcon, tab: 'Payments' },
  { label: 'Manage Users', icon: UsersIcon, tab: 'Users', adminOnly: true },
  { label: 'Print Logs', icon: ClipboardDocumentListIcon, tab: 'Settings', adminOnly: true },
  { label: 'Settings', icon: Cog6ToothIcon, tab: 'Settings' },
];

function DashboardHome({ onNavigate }) {
  const { role } = useAuth();
  const canManage = role === ROLES.ADMIN || role === ROLES.SUPER_ADMIN;
  const period = useReportingPeriod();
  const compareToPrevious = period.rangeMode === 'period' && period.schoolYear !== 'all';
  const { totalUsers, totalStudents, totalPayments, totalDisbursed, previous, loading, updatedAt } =
    useAdminStats(period.periods, { compareToPrevious });
  const dateRange = getDateRange(period.periods);
  const rangeStart = dateRange?.start ?? null;
  const rangeEnd = dateRange?.end ?? null;
  const { points: trendPoints, loading: trendLoading } = useAdminTrend(
    period.schoolYear === 'all' ? null : period.schoolYear
  );
  const { students, loading: studentsLoading } = useStudents();
  const { payments } = usePayments();
  const visibleQuickLinks = quickLinks.filter((link) => !link.adminOnly || canManage);

  const [summaryGradeFilter, setSummaryGradeFilter] = useState('');
  const [summarySectionFilter, setSummarySectionFilter] = useState('');
  const [summarySchoolYearFilter, setSummarySchoolYearFilter] = useState('');
  const [summaryStatusFilter, setSummaryStatusFilter] = useState('');
  const [openGrades, setOpenGrades] = useState(() => new Set());

  const enrolledStudents = useMemo(() => students, [students]);

  const summarySectionOptions = useMemo(() => {
    const source = summaryGradeFilter
      ? enrolledStudents.filter((s) => s.grade_level === summaryGradeFilter)
      : enrolledStudents;
    return [...new Set(source.map((s) => s.section).filter(Boolean))].sort();
  }, [enrolledStudents, summaryGradeFilter]);

  const summarySchoolYearOptions = useMemo(
    () => [...new Set(enrolledStudents.map((s) => s.school_year).filter(Boolean))].sort().reverse(),
    [enrolledStudents]
  );

  useEffect(() => {
    if (summarySectionFilter && !summarySectionOptions.includes(summarySectionFilter)) {
      setSummarySectionFilter('');
    }
  }, [summarySectionOptions, summarySectionFilter]);

  const filteredEnrolledStudents = useMemo(
    () =>
      enrolledStudents.filter((s) => {
        if (summaryGradeFilter && s.grade_level !== summaryGradeFilter) return false;
        if (summarySectionFilter && s.section !== summarySectionFilter) return false;
        if (summarySchoolYearFilter && s.school_year !== summarySchoolYearFilter) return false;
        if (summaryStatusFilter && s.status !== summaryStatusFilter) return false;
        return true;
      }),
    [enrolledStudents, summaryGradeFilter, summarySectionFilter, summarySchoolYearFilter, summaryStatusFilter]
  );

  const totalActive = useMemo(
    () => filteredEnrolledStudents.filter((s) => s.status === 'Active').length,
    [filteredEnrolledStudents]
  );
  const totalInactive = useMemo(
    () => filteredEnrolledStudents.filter((s) => s.status === 'Inactive').length,
    [filteredEnrolledStudents]
  );

  const enrolledBreakdown = useMemo(() => {
    const map = new Map();
    for (const s of filteredEnrolledStudents) {
      const key = `${s.grade_level}||${s.section || '—'}`;
      if (!map.has(key)) map.set(key, { active: 0, inactive: 0 });
      const entry = map.get(key);
      if (s.status === 'Active') entry.active += 1;
      else if (s.status === 'Inactive') entry.inactive += 1;
    }
    return [...map.entries()]
      .map(([key, { active, inactive }]) => {
        const [grade_level, section] = key.split('||');
        return { grade_level, section, active, inactive, total: active + inactive };
      })
      .sort(
        (a, b) =>
          GRADE_LEVELS.indexOf(a.grade_level) - GRADE_LEVELS.indexOf(b.grade_level) ||
          a.section.localeCompare(b.section)
      );
  }, [filteredEnrolledStudents]);

  // Enrolled students grouped by grade level, each with its sections and combined totals.
  const gradeGroups = useMemo(() => {
    const groups = new Map();
    for (const row of enrolledBreakdown) {
      if (!groups.has(row.grade_level)) {
        groups.set(row.grade_level, { grade_level: row.grade_level, active: 0, inactive: 0, total: 0, sections: [] });
      }
      const group = groups.get(row.grade_level);
      group.active += row.active;
      group.inactive += row.inactive;
      group.total += row.total;
      group.sections.push(row);
    }
    return [...groups.values()];
  }, [enrolledBreakdown]);

  const toggleGrade = (grade) =>
    setOpenGrades((prev) => {
      const next = new Set(prev);
      if (next.has(grade)) next.delete(grade);
      else next.add(grade);
      return next;
    });
  const allGradesOpen = gradeGroups.length > 0 && gradeGroups.every((g) => openGrades.has(g.grade_level));
  const toggleAllGrades = () =>
    setOpenGrades(allGradesOpen ? new Set() : new Set(gradeGroups.map((g) => g.grade_level)));

  const hasSummaryFilters = Boolean(
    summaryGradeFilter || summarySectionFilter || summarySchoolYearFilter || summaryStatusFilter
  );
  const clearSummaryFilters = () => {
    setSummaryGradeFilter('');
    setSummarySectionFilter('');
    setSummarySchoolYearFilter('');
    setSummaryStatusFilter('');
  };

  const [categorySectionFilter, setCategorySectionFilter] = useState('');
  const [categorySchoolYearFilter, setCategorySchoolYearFilter] = useState('');

  const categorySectionOptions = useMemo(
    () => [...new Set(students.map((s) => s.section).filter(Boolean))].sort(compareByGradeOrder),
    [students]
  );

  const categorySchoolYearOptions = useMemo(
    () => [...new Set(students.map((s) => s.school_year).filter(Boolean))].sort().reverse(),
    [students]
  );

  const studentSectionById = useMemo(() => {
    const map = new Map();
    for (const s of students) map.set(s.id, s.section);
    return map;
  }, [students]);

  const studentSchoolYearById = useMemo(() => {
    const map = new Map();
    for (const s of students) map.set(s.id, s.school_year);
    return map;
  }, [students]);

  const hasCategoryFilters = Boolean(categorySectionFilter || categorySchoolYearFilter);
  const clearCategoryFilters = () => {
    setCategorySectionFilter('');
    setCategorySchoolYearFilter('');
  };

  const categoryBreakdown = useMemo(() => {
    const relevantStudentCount = students.filter(
      (s) =>
        (!categorySectionFilter || s.section === categorySectionFilter) &&
        (!categorySchoolYearFilter || s.school_year === categorySchoolYearFilter)
    ).length;

    const inScope = (p) => {
      if (rangeStart) {
        const paidOn = String(p.payment_date ?? '').slice(0, 10);
        if (paidOn < rangeStart || paidOn > rangeEnd) return false;
      }
      if (categorySectionFilter) {
        if (!p.student_id || studentSectionById.get(p.student_id) !== categorySectionFilter) return false;
      }
      if (categorySchoolYearFilter) {
        // Prefer the school year captured on the payment itself — it's frozen at the
        // time of payment, unlike the student's record which can change on promotion.
        const paymentSchoolYear = p.school_year ?? (p.student_id ? studentSchoolYearById.get(p.student_id) : undefined);
        if (paymentSchoolYear !== categorySchoolYearFilter) return false;
      }
      return true;
    };

    const byCategory = new Map();
    let total = 0;
    for (const p of payments) {
      if (!inScope(p)) continue;
      const amount = Number(p.amount);
      total += amount;
      byCategory.set(p.category, (byCategory.get(p.category) ?? 0) + amount);
    }

    const rows = SCHOOL_FEES.map((fee) => {
      const collected = byCategory.get(fee.category) ?? 0;
      const expected = fee.amount * relevantStudentCount;
      const percent = expected > 0 ? Math.min(100, Math.round((collected / expected) * 100)) : 0;
      return { category: fee.category, collected, expected, percent };
    });

    // The listed fees, the discount and anything else add up to the total collected.
    const feesCollected = rows.reduce((sum, r) => sum + r.collected, 0);
    const discount = byCategory.get(DISCOUNT_CATEGORY) ?? 0;
    const other = Math.round((total - feesCollected - discount) * 100) / 100;
    return { rows, discount, other, total };
  }, [
    payments,
    students,
    categorySectionFilter,
    categorySchoolYearFilter,
    studentSectionById,
    studentSchoolYearById,
    rangeStart,
    rangeEnd,
  ]);

  const categorySummary = categoryBreakdown.rows;

  const categorySummaryTotal = useMemo(() => {
    const collected = categoryBreakdown.total;
    const expected = categorySummary.reduce((sum, c) => sum + c.expected, 0);
    const percent = expected > 0 ? Math.min(100, Math.max(0, Math.round((collected / expected) * 100))) : 0;
    return { collected, expected, percent };
  }, [categoryBreakdown.total, categorySummary]);

  // Expected target: every enrolled student owes the full set of school fees.
  const totalTarget = students.length * TOTAL_SCHOOL_FEE;
  const netBalance =
    totalPayments != null && totalDisbursed != null ? totalPayments - totalDisbursed : null;

  const shareOfCollected = (amount) =>
    totalPayments > 0 && amount != null ? Math.round((amount / totalPayments) * 100) : null;
  const collectedPercent =
    totalTarget > 0 && totalPayments != null ? Math.round((totalPayments / totalTarget) * 100) : null;

  // Change against the previous month, only for a single-month period.
  const previousNet = previous ? previous.totalPayments - previous.totalDisbursed : null;
  const previousLabel = compareToPrevious ? monthNames[(period.month + 11) % 12].slice(0, 3) : '';
  const changeFor = (current, previousValue, neutral = false) =>
    previous && current != null ? { current, previous: previousValue, previousLabel, neutral } : null;

  return (
    <>
      <ReportingPeriodBar period={period} loading={loading} updatedAt={updatedAt} />

      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard
          label="EXPECTED TARGET COLLECTION"
          value={peso(totalTarget)}
          tone={kpiTones.neutral}
          loading={studentsLoading}
          caption={`${students.length.toLocaleString()} students × ${pesoWhole(TOTAL_SCHOOL_FEE)} each`}
        />
        <KpiCard
          label="TOTAL COLLECTED"
          value={totalPayments != null ? peso(totalPayments) : null}
          tone={kpiTones.success}
          loading={loading || studentsLoading}
          caption={collectedPercent == null ? '--' : `${collectedPercent}% of ${pesoWhole(totalTarget)} target`}
          percent={collectedPercent}
          change={changeFor(totalPayments, previous?.totalPayments)}
        />
        <KpiCard
          label="TOTAL DISBURSED"
          value={totalDisbursed != null ? peso(totalDisbursed) : null}
          tone={kpiTones.neutral}
          loading={loading}
          caption={
            shareOfCollected(totalDisbursed) == null
              ? '--'
              : `${shareOfCollected(totalDisbursed)}% of funds collected utilized`
          }
          percent={shareOfCollected(totalDisbursed)}
          change={changeFor(totalDisbursed, previous?.totalDisbursed, true)}
        />
        <KpiCard
          label="NET BALANCE ON HAND"
          value={netBalance != null ? peso(netBalance) : null}
          tone={netBalance != null ? signTone(netBalance) : kpiTones.neutral}
          loading={loading}
          caption={
            shareOfCollected(netBalance) == null
              ? '--'
              : `${shareOfCollected(netBalance)}% of funds collected on hand`
          }
          percent={shareOfCollected(netBalance)}
          change={changeFor(netBalance, previousNet)}
        />
        <KpiCard
          label="TOTAL STUDENTS"
          value={totalStudents != null ? totalStudents.toLocaleString() : null}
          caption="Enrolled Students"
        />
        {canManage && (
          <KpiCard
            label="TOTAL USERS"
            value={totalUsers != null ? totalUsers.toLocaleString() : null}
            caption="Registered Officers"
          />
        )}
      </section>

      <MonthlyTrendChart
        points={trendPoints}
        loading={trendLoading}
        schoolYearText={period.schoolYear === 'all' ? null : schoolYearLabel(period.schoolYear)}
      />

      <section className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-3 sm:px-5 py-4 bg-navy-light space-y-3">
          <div>
            <h2 className="text-lg font-semibold text-white">Enrolled Students Summary</h2>
            <div className="flex flex-wrap items-center gap-2 mt-1.5">
              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-white/30 text-white">
                {filteredEnrolledStudents.length} Total
              </span>
              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-success text-white">
                {totalActive} Active
              </span>
              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-white/10 text-gray-200">
                {totalInactive} Inactive
              </span>
            </div>
          </div>

          <div className="border-t border-white/20" />

          <div className="flex flex-wrap items-center gap-2 w-full">
            <select
              value={summaryGradeFilter}
              onChange={(e) => setSummaryGradeFilter(e.target.value)}
              className="flex-1 min-w-[45%] sm:min-w-[140px] rounded-md border-0 px-3 py-2 text-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold"
            >
              <option value="">All Grades</option>
              {GRADE_LEVELS.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>

            <select
              value={summarySectionFilter}
              onChange={(e) => setSummarySectionFilter(e.target.value)}
              className="flex-1 min-w-[45%] sm:min-w-[140px] rounded-md border-0 px-3 py-2 text-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold"
            >
              <option value="">All Sections</option>
              {summarySectionOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>

            <select
              value={summaryStatusFilter}
              onChange={(e) => setSummaryStatusFilter(e.target.value)}
              className="flex-1 min-w-[45%] sm:min-w-[140px] rounded-md border-0 px-3 py-2 text-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold"
            >
              <option value="">All Statuses</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>

            <select
              value={summarySchoolYearFilter}
              onChange={(e) => setSummarySchoolYearFilter(e.target.value)}
              className="flex-1 min-w-[45%] sm:min-w-[140px] rounded-md border-0 px-3 py-2 text-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold"
            >
              <option value="">All School Years</option>
              {summarySchoolYearOptions.map((sy) => (
                <option key={sy} value={sy}>
                  {sy}
                </option>
              ))}
            </select>

            <button
              type="button"
              onClick={clearSummaryFilters}
              disabled={!hasSummaryFilters}
              className="w-full sm:w-auto text-lg text-gray-200 border border-gray-300 px-3 py-2 rounded-md hover:text-white whitespace-nowrap disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed disabled:hover:text-gray-200"
            >
              Clear filters
            </button>
          </div>
        </div>

        <div className="p-5">
          {gradeGroups.length === 0 ? (
            <p className="text-sm text-muted text-center py-4">No enrolled students match this filter</p>
          ) : (
            <>
              <div className="mb-3 flex justify-end">
                <button
                  type="button"
                  onClick={toggleAllGrades}
                  className="text-sm font-medium text-navy underline underline-offset-2 hover:text-navy-light"
                >
                  {allGradesOpen ? 'Collapse all' : 'Expand all'}
                </button>
              </div>

              <div className="divide-y divide-gray-200 overflow-hidden rounded-lg border border-gray-200">
                {gradeGroups.map((group) => {
                  const isOpen = openGrades.has(group.grade_level);
                  const panelId = `enrolled-${group.grade_level.replace(/\s+/g, '-')}`;
                  return (
                    <div key={group.grade_level}>
                      <button
                        type="button"
                        onClick={() => toggleGrade(group.grade_level)}
                        aria-expanded={isOpen}
                        aria-controls={panelId}
                        className="flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-gold/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-navy-light"
                      >
                        <ChevronRightIcon
                          className={`h-5 w-5 shrink-0 text-gray-500 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                          aria-hidden="true"
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block font-semibold text-gray-900">{group.grade_level}</span>
                          <span className="block text-sm text-muted">
                            {group.sections.length} {group.sections.length === 1 ? 'section' : 'sections'}
                          </span>
                        </span>
                        <span className="flex shrink-0 items-center gap-3 text-sm sm:gap-5">
                          <span className="hidden sm:inline font-medium text-success">{group.active} Active</span>
                          <span className="hidden sm:inline font-medium text-gray-500">{group.inactive} Inactive</span>
                          <span className="font-semibold text-gray-900">{group.total} Total</span>
                        </span>
                      </button>

                      <div id={panelId} hidden={!isOpen} className="bg-gray-50 px-4 py-2">
                        <table className="w-full text-sm text-left border-collapse">
                          <thead>
                            <tr className="border-b border-gray-200 text-gray-500 uppercase">
                              <th className="py-2 pr-4 font-semibold">Section</th>
                              <th className="py-2 pr-4 font-semibold text-right">Active</th>
                              <th className="py-2 pr-4 font-semibold text-right">Inactive</th>
                              <th className="py-2 font-semibold text-right">Total</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {group.sections.map((row) => (
                              <tr key={row.section}>
                                <td className="py-2 pr-4 text-gray-900">{row.section}</td>
                                <td className="py-2 pr-4 text-right font-medium text-success">{row.active}</td>
                                <td className="py-2 pr-4 text-right font-medium text-gray-500">{row.inactive}</td>
                                <td className="py-2 text-right font-semibold text-gray-900">{row.total}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </section>

      <section className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-3 sm:px-5 py-4 bg-navy-light space-y-3">
          <div>
            <h2 className="text-lg font-semibold text-white">Payment Categories Summary</h2>
            <p className="text-sm text-gray-200">All Categories · {period.label}</p>
          </div>

          <div className="border-t border-white/20" />

          <div className="flex flex-wrap items-center gap-2 w-full">
            <select
              value={categorySectionFilter}
              onChange={(e) => setCategorySectionFilter(e.target.value)}
              className="flex-1 min-w-[45%] sm:min-w-[140px] rounded-md border-0 px-3 py-2 text-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold"
            >
              <option value="">All Sections</option>
              {categorySectionOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>

            <select
              value={categorySchoolYearFilter}
              onChange={(e) => setCategorySchoolYearFilter(e.target.value)}
              className="flex-1 min-w-[45%] sm:min-w-[140px] rounded-md border-0 px-3 py-2 text-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold"
            >
              <option value="">All School Years</option>
              {categorySchoolYearOptions.map((sy) => (
                <option key={sy} value={sy}>
                  {sy}
                </option>
              ))}
            </select>

            <button
              type="button"
              onClick={clearCategoryFilters}
              disabled={!hasCategoryFilters}
              className="w-full sm:w-auto text-lg text-gray-200 border border-gray-300 px-3 py-2 rounded-md hover:text-white whitespace-nowrap disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed disabled:hover:text-gray-200"
            >
              Clear filters
            </button>
          </div>
        </div>

        <div className="p-5">
          <div className="space-y-4">
            {categorySummary.map(({ category, collected, percent }) => (
              <div key={category}>
                <div className="flex justify-between items-baseline text-sm mb-1 gap-2">
                  <span className="font-medium text-gray-800">{category}</span>
                  <span className="text-gray-500 whitespace-nowrap">
                    {peso(collected)}{' '}
                    <span className="font-medium text-navy">({percent}%)</span>
                  </span>
                </div>
                <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      percent >= 100 ? 'bg-success' : 'bg-gold'
                    }`}
                    style={{ width: `${percent}%` }}
                  />
                </div>
              </div>
            ))}
          </div>

          <CategoryAdjustments discount={categoryBreakdown.discount} other={categoryBreakdown.other} />

          <div
            className={`mt-5 p-4 rounded-lg ${
              categorySummaryTotal.percent >= 100 ? 'bg-success/10' : 'bg-gold/10'
            }`}
          >
            <div className="flex justify-between items-baseline gap-2 mb-2">
              <span
                className={`text-lg font-bold ${
                  categorySummaryTotal.percent >= 100 ? 'text-success' : 'text-navy'
                }`}
              >
                TOTAL
              </span>
              <span
                className={`text-lg font-bold whitespace-nowrap ${
                  categorySummaryTotal.percent >= 100 ? 'text-success' : 'text-navy'
                }`}
              >
                {peso(categorySummaryTotal.collected)} ({categorySummaryTotal.percent}%)
              </span>
            </div>
            <div className="w-full h-3 bg-white/60 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  categorySummaryTotal.percent >= 100 ? 'bg-success' : 'bg-gold'
                }`}
                style={{ width: `${categorySummaryTotal.percent}%` }}
              />
            </div>
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-sm font-medium text-muted uppercase tracking-wide mb-3">
          Quick Links
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {visibleQuickLinks.map(({ label, icon: Icon, tab }) => (
            <button
              key={label}
              type="button"
              onClick={() => onNavigate?.(tab)}
              className="flex flex-col items-center gap-2 bg-white rounded-lg border border-gray-200 shadow-sm p-5 hover:border-gold hover:shadow-md transition"
            >
              <Icon className="h-6 w-6 text-navy" />
              <span className="text-sm text-ink">{label}</span>
            </button>
          ))}
        </div>
      </section>
    </>
  );
}

export default DashboardHome;
